import React, { useEffect } from 'react';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { IProduct } from '../../../../../utils/types/contentful';
import {
  ProductMainVariantDetailsHeader,
  ProductMainVariantDetailsHeaderProductPrice,
} from './Header/productMainVariantDetailsHeader';
import { ProductMainColorPicker } from './ColorPicker/productMainColorPicker';
import { ProductMainDiopterPicker } from './DiopterPicker/ProductMainDiopterPicker';
import { ProductMainSizePicker } from './SizePicker/ProductMainSizePicker';
import { ProductMainPricePicker } from './PricePicker/productMainPricePicker';
import { ProductMainVariantDetailsInfos } from './Infos/productMainDetailsInfos';
import { ProductMainOptions } from './Options/productMainOptions';
import { ProductMainRecommendations } from './Recommendations/productMainRecommendations';
import { ProductMainKitSave } from './KitSave/productMainKitSave';
import { formatContentfulComponentProps } from '../../../../../utils/globals/index';
import { BuyProductButton } from '../../../BuyButton/buyProduct';
import { ProductMainSubHeader } from './SubHeader/productMainSubHeader';
import { SubHeaderState } from '../../../../../utils/recoil/subHeader';
import { useMediaQuery } from '../../../../../utils/hooks/useMediaQuery';
import { ProductStarReviews } from '../../../../Marketing/Reviews/Trustpilot/trustpilotProductStarReviews';

export interface ProductMainVariantDetailsProps {
  product: IProduct;
}

export const ProductMainVariantDetails: React.FC<
  ProductMainVariantDetailsProps
> = ({
  addToCartButtons,
  colorPicker,
  diopterPicker,
  option,
  pricePicker,
  product,
  recommendation,
  sizePicker,
  variant,
  kitSave,
}) => {
  const setSubHeader = useSetRecoilState(SubHeaderState);
  const resetSubHeader = useResetRecoilState(SubHeaderState);

  const category = product.categories && product.categories[0];
  const isWindowSmallSize: boolean = useMediaQuery('lg');

  useEffect(() => {
    setSubHeader({
      children: (
        <ProductMainSubHeader
          button={returnBuyButton()}
          price={headerProductPrice}
        />
      ),
      placement: isWindowSmallSize ? 'bottom' : 'top',
      isDefaultScroll: isWindowSmallSize,
    });
    return () => {
      resetSubHeader();
    };
  }, [isWindowSmallSize]);

  const returnBuyButton = () => (
    <BuyProductButton
      color={category?.color}
      buttons={formatContentfulComponentProps(addToCartButtons)}
      fullWidth
    />
  );

  const headerProductPrice = (
    <ProductMainVariantDetailsHeaderProductPrice
      size='2xl'
      category={category}
    />
  );

  return (
    <div className='relative h-full w-full pb-8'>
      <ProductMainVariantDetailsHeader
        category={category}
        label={product.label}
        subLabel={product.subLabel}
      />
      {/* Permanent variants */}
      {colorPicker && (
        <ProductMainColorPicker
          hasBorder
          variants={product.variants}
          productSize={variant.size?.value}
          selectedColor={variant.color}
          labelDetails={variant?.labelDetails}
          {...formatContentfulComponentProps(colorPicker)}
        />
      )}
      {diopterPicker && (
        <ProductMainDiopterPicker
          variant={variant}
          category={category}
          {...formatContentfulComponentProps(diopterPicker)}
        />
      )}
      {sizePicker && (
        <ProductMainSizePicker
          variant={variant}
          sizes={product.sizes}
          category={category}
          hasBorder
          {...formatContentfulComponentProps(sizePicker)}
        />
      )}
      {pricePicker && (
        <ProductMainPricePicker
          variant={variant}
          variants={product.variants}
          category={category}
          {...formatContentfulComponentProps(pricePicker)}
        />
      )}
      {addToCartButtons && !isWindowSmallSize && (
        <div className='my-4'>
          <BuyProductButton
            color={category?.color}
            buttons={formatContentfulComponentProps(addToCartButtons)}
          />
        </div>
      )}
      {((option && variant?.option) || product?.isOptionCheck) && (
        <ProductMainOptions
          {...formatContentfulComponentProps(option)}
          color={category?.color}
        />
      )}
      {kitSave && (
        <ProductMainKitSave {...formatContentfulComponentProps(kitSave)} />
      )}
      <ProductMainVariantDetailsInfos informations={product.informations} />
      {product.recommendations && (
        <ProductMainRecommendations
          {...formatContentfulComponentProps(recommendation)}
          recommendations={product.recommendations}
        />
      )}
      <div className='absolute opacity-0 sm:opacity-1 z-10 bottom-12 -left-8'>
        <ProductStarReviews />
      </div>
    </div>
  );
};
